import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import { CspTier,
    Organization,
    OrganizationAddressSuggestion,
} from "@/types/organization";
import { User } from "@/types/user";
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Autocomplete,
    AutocompleteItem,
    Button,
    Divider,
    Chip,
} from "@heroui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { InferType, number, object, string } from "yup";
import TextInput from "../inputs/TextInput";
import SelectButtonInput from "../inputs/SelectButtonInput";
import { Link } from "@tanstack/react-router";
import AutoCompleteInput from "@/components/inputs/AutoCompleteInput";

const schema = object().shape({
    name: string().required(),
    group_type: string().required(),
    service_provider: number().nullable(),
    hubspot_link: string().nullable(),
    address_1: string().nullable(),
    address_2: string().nullable(),
    city: string().nullable(),
    state: string().nullable(),
    zip_code: string().nullable(),
    account_manager: number().nullable(),
    csp_tier: string().oneOf(Object.values(CspTier)).nullable(),
});

function AddEditOrganizationForm({
    existingOrganization,
    onClose,
    farmOnly = false,
}: {
    existingOrganization?: Organization;
    onClose: (
        shouldRefetch: boolean,
        createdOrganization?: Pick<Organization, "id" | "name">,
    ) => void;
    farmOnly?: boolean;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const [addressSearch, setAddressSearch] = useState("");
    const [debouncedAddressSearch, setDebouncedAddressSearch] = useState("");
    const [selectedAddressSuggestion, setSelectedAddressSuggestion] =
        useState<OrganizationAddressSuggestion | null>(null);
    const { userIsSentinel, isConsultant, user } = useAuth();
    const canEditCspTier = userIsSentinel && Boolean(user?.is_staff);

    const isEdit = !!existingOrganization;
    const canSearchOrganizationAddress =
        user?.is_staff === true ||
        userIsSentinel ||
        (isConsultant &&
            (user?.organization as Organization)?.group_type ===
                "service provider");
    const hasActiveSelectedAddress =
        selectedAddressSuggestion !== null &&
        addressSearch.trim() === selectedAddressSuggestion.label;

    useEffect(() => {
        const query = addressSearch.trim();
        if (
            hasActiveSelectedAddress ||
            query.length < 3 ||
            query.length > 256
        ) {
            setDebouncedAddressSearch("");
            return;
        }

        const timeoutId = setTimeout(
            () => setDebouncedAddressSearch(query),
            350,
        );
        return () => clearTimeout(timeoutId);
    }, [addressSearch, hasActiveSelectedAddress]);

    const addressSuggestionsQuery = useGetRequest<
        OrganizationAddressSuggestion[]
    >({
        url: "organizations/address_search/",
        params: { q: debouncedAddressSearch },
        enabled:
            canSearchOrganizationAddress &&
            !hasActiveSelectedAddress &&
            debouncedAddressSearch.length >= 3 &&
            debouncedAddressSearch.length <= 256,
        queryOptions: { retry: false },
    });

    const addressSuggestionItems = useMemo(() => {
        if (hasActiveSelectedAddress && selectedAddressSuggestion) {
            return [selectedAddressSuggestion];
        }

        if (addressSearch.trim() !== debouncedAddressSearch) {
            return [];
        }

        return addressSuggestionsQuery.data ?? [];
    }, [
        addressSearch,
        addressSuggestionsQuery.data,
        debouncedAddressSearch,
        hasActiveSelectedAddress,
        selectedAddressSuggestion,
    ]);

    const f = useForm<InferType<typeof schema>>({
        resolver: yupResolver(schema),
        defaultValues: farmOnly && !isEdit ? { group_type: "farm" } : undefined,
    });

    const accountManagersQuery = useGetRequest<User[]>({
        url: "users/account_managers/",
        enabled: userIsSentinel,
    });

    const accountManagers = useMemo(() => {
        return (
            accountManagersQuery.data?.map((user) => ({
                id: user.id,
                description: user.full_name,
            })) ?? []
        );
    }, [accountManagersQuery.data]);

    const groupType = f.watch("group_type");

    useEffect(() => {
        if (existingOrganization) {
            f.reset({
                name: existingOrganization.name,
                group_type: existingOrganization.group_type,
                service_provider:
                    existingOrganization.service_provider?.id ?? null,
                hubspot_link: existingOrganization.hubspot_link ?? "",
                address_1: existingOrganization.address_1 ?? "",
                address_2: existingOrganization.address_2 ?? "",
                city: existingOrganization.city ?? "",
                state: existingOrganization.state ?? "",
                zip_code: existingOrganization.zip_code ?? "",
                account_manager: existingOrganization.account_manager ?? null,
                csp_tier: existingOrganization.csp_tier ?? null,
            });
        }
    }, [existingOrganization, f]);

    const serviceProvidersQuery = useGetRequest<Organization[]>({
        url: "organizations/service_providers/",
        enabled: userIsSentinel && groupType === "farm",
    });

    const orgUsersQuery = useGetRequest<User[]>({
        url: existingOrganization
            ? `organizations/${existingOrganization.id}/users/`
            : "",
        enabled: !!existingOrganization,
    });

    const groupTypeOptions = useMemo(() => {
        if (
            existingOrganization?.id ===
            (user?.organization as Organization)?.id
        ) {
            if (userIsSentinel) {
                return [];
            }
            if (isConsultant) {
                return [{ id: "service provider", description: "CSP" }];
            }
            return [{ id: "farm", description: "Farm" }];
        }
        if (userIsSentinel) {
            return [
                { id: "farm", description: "Farm" },
                { id: "service provider", description: "CSP" },
            ];
        }
        if (isConsultant) {
            return [{ id: "farm", description: "Farm" }];
        }
        return [];
    }, [
        existingOrganization?.id,
        isConsultant,
        user?.organization,
        userIsSentinel,
    ]);

    const serviceProviderOptions = useMemo(() => {
        return (
            serviceProvidersQuery.data?.map((sp) => ({
                id: sp.id,
                description: sp.name,
            })) ?? []
        );
    }, [serviceProvidersQuery.data]);

    const createMutation = useMutateRequest<Pick<Organization, "id" | "name">>({
        successMessage: "Organization created",
        errorMessage: "Failed to create organization",
    });

    const updateMutation = useMutateRequest({
        successMessage: "Organization updated",
        errorMessage: "Failed to update organization",
    });

    const onSubmit = (data: InferType<typeof schema>) => {
        const { service_provider, csp_tier, ...values } = data;
        const organizationData = {
            ...values,
            ...(canEditCspTier && data.group_type === "service provider"
                ? { csp_tier: csp_tier ?? null }
                : {}),
        };
        if (isEdit && existingOrganization) {
            const existingServiceProviderId =
                existingOrganization.service_provider?.id ?? null;

            const selectedServiceProviderId = userIsSentinel
                ? data.group_type === "farm"
                    ? (service_provider ?? null)
                    : null
                : existingServiceProviderId;

            const serviceProviderChanged =
                userIsSentinel &&
                selectedServiceProviderId !== existingServiceProviderId;

            const isChangingAwayFromFarm =
                existingOrganization.group_type === "farm" &&
                data.group_type !== "farm";

            const updateOrganization = () => {
                updateMutation.mutate(
                    {
                        url: `organizations/${existingOrganization.id}/`,
                        method: "patch",
                        data: organizationData,
                    },
                    { onSuccess: () => onClose(true) },
                );
            };

            const updateServiceProvider = (onSuccess: () => void) => {
                updateMutation.mutate(
                    {
                        url: `organizations/${existingOrganization.id}/service_provider/`,
                        method: "put",
                        data: {
                            service_provider: selectedServiceProviderId,
                        },
                    },
                    { onSuccess },
                );
            };

            if (serviceProviderChanged && isChangingAwayFromFarm) {
                updateServiceProvider(updateOrganization);
                return;
            }

            updateMutation.mutate(
                {
                    url: `organizations/${existingOrganization.id}/`,
                    method: "patch",
                    data: organizationData,
                },
                {
                    onSuccess: () => {
                        if (serviceProviderChanged) {
                            updateServiceProvider(() => onClose(true));
                        } else {
                            onClose(true);
                        }
                    },
                },
            );

            return;
        }

        createMutation.mutate(
            {
                url: "organizations/",
                method: "post",
                data: {
                    ...organizationData,
                    service_provider:
                        userIsSentinel && data.group_type === "farm"
                            ? service_provider
                            : null,
                },
            },
            { onSuccess: (organization) => onClose(true, organization) },
        );
    };

    return (
        <FormProvider {...f}>
            <Modal
                isOpen
                onClose={() => onClose(false)}
                isDismissable={false}
                size="lg"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader>
                        {isEdit
                            ? "Edit Organization"
                            : farmOnly
                              ? "Add Farm"
                              : "Add Organization"}
                    </ModalHeader>
                    <ModalBody className="flex flex-col gap-2">
                        {(!farmOnly || isEdit) && (
                            <SelectButtonInput
                                name="group_type"
                                label="Type"
                                options={groupTypeOptions}
                                orientation="horizontal"
                                classNames={{
                                    base: "flex-1 w-full",
                                    wrapper: "grid grid-cols-2",
                                }}
                                isDisabled={isEdit}
                            />
                        )}
                        <TextInput
                            name="name"
                            label="Name"
                            labelPlacement="outside"
                            size="sm"
                            placeholder=" "
                        />
                        {canSearchOrganizationAddress && (
                            <Autocomplete
                                label="Search Address"
                                labelPlacement="outside"
                                size="sm"
                                placeholder="Start typing an address"
                                allowsCustomValue={false}
                                inputProps={{ maxLength: 256 }}
                                inputValue={addressSearch}
                                items={addressSuggestionItems}
                                selectedKey={
                                    selectedAddressSuggestion?.id ?? null
                                }
                                isLoading={addressSuggestionsQuery.isFetching}
                                listboxProps={{
                                    emptyContent:
                                        addressSuggestionsQuery.isError
                                            ? "Address search is unavailable"
                                            : addressSearch.trim().length < 3
                                              ? "Enter at least 3 characters"
                                              : "No addresses found",
                                }}
                                onInputChange={(value) => {
                                    if (
                                        selectedAddressSuggestion !== null &&
                                        value.trim() !==
                                            selectedAddressSuggestion.label
                                    ) {
                                        setSelectedAddressSuggestion(null);
                                    }
                                    setAddressSearch(value);
                                }}
                                onSelectionChange={(key) => {
                                    if (key === null) {
                                        setSelectedAddressSuggestion(null);
                                        return;
                                    }

                                    const suggestion =
                                        addressSuggestionItems.find(
                                            ({ id }) => id === String(key),
                                        );
                                    if (!suggestion) return;

                                    setSelectedAddressSuggestion(suggestion);
                                    setAddressSearch(suggestion.label);
                                    f.setValue(
                                        "address_1",
                                        suggestion.address_1,
                                        {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        },
                                    );
                                    f.setValue("city", suggestion.city, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                    f.setValue("state", suggestion.state, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                    f.setValue(
                                        "zip_code",
                                        suggestion.zip_code,
                                        {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        },
                                    );
                                }}
                            >
                                {(suggestion) => (
                                    <AutocompleteItem
                                        key={suggestion.id}
                                        textValue={suggestion.label}
                                    >
                                        {suggestion.label}
                                    </AutocompleteItem>
                                )}
                            </Autocomplete>
                        )}
                        <TextInput
                            name="address_1"
                            label="Address 1"
                            labelPlacement="outside"
                            size="sm"
                            placeholder=" "
                        />
                        <TextInput
                            name="address_2"
                            label="Address 2"
                            labelPlacement="outside"
                            size="sm"
                            placeholder=" "
                        />
                        <div className="grid grid-cols-3 gap-2">
                            <TextInput
                                name="city"
                                label="City"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                            <TextInput
                                name="state"
                                label="State"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                            <TextInput
                                name="zip_code"
                                label="Zip"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                        </div>
                        {userIsSentinel && groupType === "farm" && (
                            <AutoCompleteInput
                                name="service_provider"
                                label="Service Provider"
                                options={serviceProviderOptions}
                                labelPlacement="outside"
                                size="sm"
                            />
                        )}
                        {canEditCspTier && groupType === "service provider" && (
                            <AutoCompleteInput
                                name="csp_tier"
                                label="CSP tier"
                                labelPlacement="outside"
                                size="sm"
                                placeholder="Unassigned"
                                isClearable
                                options={[
                                    { id: CspTier.SCOUT, description: "Scout" },
                                    {
                                        id: CspTier.GUARDIAN,
                                        description: "Guardian",
                                    },
                                    {
                                        id: CspTier.SENTINEL_ELITE,
                                        description: "Sentinel Elite",
                                    },
                                ]}
                            />
                        )}
                        {userIsSentinel && groupType === "service provider" && (
                            <AutoCompleteInput
                                name="account_manager"
                                label="Account Manager"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                                options={accountManagers}
                            />
                        )}
                        {userIsSentinel && isEdit && (
                            <TextInput
                                name="hubspot_link"
                                label="Hubspot Link"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                                isReadOnly
                            />
                        )}
                        {isEdit &&
                            orgUsersQuery.data &&
                            orgUsersQuery.data.length > 0 && (
                                <div className="flex flex-col gap-1">
                                    <div className="text-xs text-gray-500">
                                        Users ({orgUsersQuery.data.length})
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {orgUsersQuery.data.map((u) => (
                                            <Link
                                                key={u.id}
                                                to="/dashboard/network/users/$userId"
                                                params={{
                                                    userId: u.id.toString(),
                                                }}
                                            >
                                                <Chip
                                                    size="sm"
                                                    radius="sm"
                                                    variant="flat"
                                                    className="cursor-pointer"
                                                >
                                                    {u.full_name || u.username}
                                                </Chip>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        <Divider />
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            size="sm"
                            variant="flat"
                            onPress={() => onClose(false)}
                            fullWidth
                            isDisabled={
                                createMutation.isPending ||
                                updateMutation.isPending
                            }
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            color="primary"
                            onPress={() => {
                                f.trigger();
                                f.handleSubmit(onSubmit)();
                            }}
                            fullWidth
                            isLoading={
                                createMutation.isPending ||
                                updateMutation.isPending
                            }
                        >
                            {isEdit ? "Update" : "Create"}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </FormProvider>
    );
}

export default AddEditOrganizationForm;
